import { useState } from 'react';
import { connect, ConnectedProps } from 'react-redux';

import SessionClientDashboard from 'dashboard/lib/clients/SessionClientDashboard';
import { MessageType } from 'dashboard/lib/interfaces/Message';
import {
  BlueprintKey,
  BlueprintServerMetadata,
  IndustryName,
  UseCaseV2Name,
} from 'dashboard/models/blueprint';
import { InquiryStatus } from 'dashboard/models/inquiry';
import { BuilderName } from 'dashboard/models/inquiry-template';
import { User } from 'dashboard/models/user';
import { showMessage } from 'dashboard/redux/actions/messages';
import { RootState } from 'dashboard/redux/store';
import {
  addBlueprint,
  loadBlueprintPreview,
  loadBlueprints,
} from 'dashboard/redux/thunk-actions/blueprints';
import { loadCasesList } from 'dashboard/redux/thunk-actions/cases';
import { loadInquiryList } from 'dashboard/redux/thunk-actions/inquiries';
import { loadTemplateBuilderPreview } from 'dashboard/redux/thunk-actions/inquiry-templates';
import {
  loadOrganization,
  updateOnboardingChecklistSandboxCompletedSteps,
} from 'dashboard/redux/thunk-actions/organization';
import { loadReportsList } from 'dashboard/redux/thunk-actions/reports';
import { loadWorkflowRunList } from 'dashboard/redux/thunk-actions/workflow-runs';
import DashboardSandboxHomeView from 'dashboard/views/DashboardSandboxHomeView';
import { SegmentClient } from 'lib/clients/SegmentClient';
import useApiCallback from 'lib/hooks/useApiCallback';
import useApiErrorHandler from 'lib/hooks/useApiErrorHandler';
import useApiLoader from 'lib/hooks/useApiLoader';
import { ApiEndpoint } from 'lib/interfaces/api';
import { isPermissionsError, permissionsErrorMessage } from 'lib/permissions-errors';

const DashboardSandboxHomeRoute = ({
  blueprints,
  blueprintPreviews,
  casesForSelectedBlueprint,
  currentEnvironment,
  inquiriesForSelectedBlueprint,
  inquiryTemplateBuilderPreviews,
  organization,
  currentOrganizationPlan,
  reportsForSelectedBlueprint,
  workflowRunsForSelectedBlueprint,
  addBlueprint,
  loadBlueprints,
  loadBlueprintPreview,
  loadCasesList,
  loadInquiryList,
  loadInquiryTemplateBuilderPreview,
  loadOrganization,
  loadReportsList,
  loadWorkflowRunList,
  updateOnboardingChecklistSandboxCompletedSteps,
  showMessage,
}: PropsFromRedux) => {
  const { loading: isLoadingPreviouslyUsedBlueprints } = useApiLoader({
    loader: loadBlueprints,
    deps: [],
    onError: (error) => {
      if (isPermissionsError(error)) {
        showMessage(permissionsErrorMessage(error), MessageType.Failure);
      } else {
        showMessage('There was an error loading this page. Please try again.', MessageType.Failure);
      }
    },
    onNetworkError: () => {
      showMessage('We are experiencing network issues. Please try again.', MessageType.Failure);
    },
  });

  const [blueprintsCatalogServer, setBlueprintsCatalogServer] = useState([]);
  const { loading: isLoadingBlueprintsCatalog } = useApiLoader({
    loader: async () => {
      const data = await SessionClientDashboard.getResourceMeta<BlueprintServerMetadata[]>(
        ApiEndpoint.Blueprint,
        'catalog',
      );
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      setBlueprintsCatalogServer(data.meta['blueprints-catalog']);
    },
    deps: [],
    onError: (error) => {
      if (isPermissionsError(error)) {
        showMessage(permissionsErrorMessage(error), MessageType.Failure);
      } else {
        showMessage('There was an error loading this page. Please try again.', MessageType.Failure);
      }
    },
    onNetworkError: () => {
      showMessage('There was an error loading this page. Please try again.', MessageType.Failure);
    },
  });

  const [users, setUsers] = useState<User[]>();
  const {
    callback: handleLoadUsers,
    error: loadUsersError,
    loading: isLoadingUsers,
  } = useApiCallback(async () => {
    const { data: users } = await SessionClientDashboard.listResource<User>(ApiEndpoint.User);
    setUsers(users);
  }, []);

  useApiErrorHandler({
    error: loadUsersError,
    onNetworkError: () =>
      showMessage('We are experiencing network issues. Please try again.', MessageType.Failure),
    onError: () =>
      showMessage(
        'There was an error loading the users for this organization.',
        MessageType.Failure,
      ),
  });

  const {
    callback: handleLoadInquiriesForSelectedBlueprint,
    error: loadInquiriesForSelectedBlueprintError,
    loading: isLoadingInquiriesForSelectedBlueprint,
  } = useApiCallback(async (inquiryTemplateIds: string[]) => {
    // NB: We always show the 25 most recent Inquiries in a terminal state matching the Inquiry Template on this page
    const currentPage = 1;
    const pageSize = 25;

    await loadInquiryList({
      currentEnvironment,
      currentPage,
      pageSize,
      filters: {
        templateIds: inquiryTemplateIds,
        status: [
          InquiryStatus.Completed,
          InquiryStatus.NeedsReview,
          InquiryStatus.Approved,
          InquiryStatus.Declined,
          InquiryStatus.Failed,
        ],
      },
      sortParams: {
        sort: 'created_at',
        shouldSortDescending: true,
      },
    });
  }, []);

  useApiErrorHandler({
    error: loadInquiriesForSelectedBlueprintError,
    onNetworkError: () =>
      showMessage('We are experiencing network issues. Please try again.', MessageType.Failure),
    onError: () =>
      showMessage(
        'There was an error loading the Inquiries associated with the selected solution.',
        MessageType.Failure,
      ),
  });

  const {
    callback: handleLoadWorkflowRunsForSelectedBlueprint,
    error: loadWorkflowRunsForSelectedBlueprintError,
    loading: isLoadingWorkflowRunsForSelectedBlueprint,
  } = useApiCallback(async (workflowIds: string[]) => {
    const currentPage = 1;
    const pageSize = 25;

    await loadWorkflowRunList({
      currentEnvironment,
      currentPage,
      pageSize,
      filters: {
        workflowIds,
        workflowVersionIds: [],
        completedAtStart: undefined,
        completedAtEnd: undefined,
        omni: undefined,
        status: [],
      },
      sortParams: {
        sort: 'created_at',
        shouldSortDescending: true,
      },
    });
  }, []);

  useApiErrorHandler({
    error: loadWorkflowRunsForSelectedBlueprintError,
    onNetworkError: () =>
      showMessage('We are experiencing network issues. Please try again.', MessageType.Failure),
    onError: () =>
      showMessage(
        'There was an error loading the Workflow Runs associated with the selected solution.',
        MessageType.Failure,
      ),
  });

  const {
    callback: handleLoadReportsForSelectedBlueprint,
    error: loadReportsForSelectedBlueprintError,
    loading: isLoadingReportsForSelectedBlueprint,
  } = useApiCallback(async (reportTemplateIds: string[]) => {
    const currentPage = 1;
    const pageSize = 25;

    await loadReportsList({
      currentEnvironment,
      currentPage,
      pageSize,
      filters: {
        reportTemplateIds,
      },
      sortParams: {
        sort: 'created_at',
        shouldSortDescending: true,
      },
    });
  }, []);

  useApiErrorHandler({
    error: loadReportsForSelectedBlueprintError,
    onNetworkError: () =>
      showMessage('We are experiencing network issues. Please try again.', MessageType.Failure),
    onError: () =>
      showMessage(
        'There was an error loading the Reports associated with the selected solution.',
        MessageType.Failure,
      ),
  });

  const {
    callback: handleLoadCasesForSelectedBlueprint,
    error: loadCasesForSelectedBlueprintError,
    loading: isLoadingCasesForSelectedBlueprint,
  } = useApiCallback(async (caseTemplateIds: string[]) => {
    const currentPage = 1;
    const pageSize = 25;

    await loadCasesList({
      currentEnvironment,
      currentPage,
      pageSize,
      filters: {
        caseTemplateIds,
      },
      sortParams: {
        sort: 'created_at',
        shouldSortDescending: true,
      },
    });
  }, []);

  useApiErrorHandler({
    error: loadCasesForSelectedBlueprintError,
    onNetworkError: () =>
      showMessage('We are experiencing network issues. Please try again.', MessageType.Failure),
    onError: () =>
      showMessage(
        'There was an error loading the Cases associated with the selected solution.',
        MessageType.Failure,
      ),
  });

  const {
    callback: handleSaveOnboardingChecklistSandboxCompletedStep,
    error: saveOnboardingChecklistSandboxCompletedStepError,
    loading: isSavingCompletedStep,
  } = useApiCallback(
    async (stepKey: string) => {
      await updateOnboardingChecklistSandboxCompletedSteps([
        stepKey,
        ...organization.attributes['onboarding-checklist-sandbox-completed-steps'],
      ]);

      SegmentClient.sendSegmentTrackEvent('Onboarding Checklist - Step Completed', {
        stepKey: stepKey,
      });
    },
    [organization],
  );

  useApiErrorHandler({
    error: saveOnboardingChecklistSandboxCompletedStepError,
    onNetworkError: () =>
      showMessage('We are experiencing network issues. Please try again.', MessageType.Failure),
    onError: () =>
      showMessage('Failed to save completed step. Please try again.', MessageType.Failure),
  });

  // For Blueprints quiz V2
  const [blueprintsQuizV2SelectedIndustry, setBlueprintsQuizV2SelectedIndustry] =
    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    useState<IndustryName>(null);
  const [blueprintsQuizV2SelectedUseCase, setBlueprintsQuizV2SelectedUseCase] =
    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    useState<UseCaseV2Name>(null);
  /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
  const [blueprintsQuizV2Result, setBlueprintsQuizV2Result] = useState<BlueprintKey>(null);

  // For Blueprints quiz V2 - industry question
  const handleBlueprintsQuizV2IndustrySubmit = (selectedOption: IndustryName) => {
    setBlueprintsQuizV2SelectedIndustry(selectedOption);

    SegmentClient.sendSegmentTrackEvent('Blueprints Quiz V2 - Industry Submitted', {
      screen: 'industry',
      industry: selectedOption,
    });
  };

  // For Blueprints quiz V2 - use cases question
  const handleBlueprintsQuizV2UseCaseSubmit = (selectedOption: UseCaseV2Name) => {
    setBlueprintsQuizV2SelectedUseCase(selectedOption);

    SegmentClient.sendSegmentTrackEvent('Blueprints Quiz V2 - Use Case Submitted', {
      screen: 'use_case',
      useCase: selectedOption,
    });

    // NB: Only surface Blueprints that are isAvailableSelfServe here
    if (blueprintsQuizV2SelectedIndustry === IndustryName.Crypto) {
      setBlueprintsQuizV2Result(BlueprintKey.KycCryptoAndWalletWatchlist);
    } else if (selectedOption === UseCaseV2Name.DocumentVerification) {
      setBlueprintsQuizV2Result(BlueprintKey.KycAndAddress);
    } else if (selectedOption === UseCaseV2Name.AgeVerification) {
      setBlueprintsQuizV2Result(BlueprintKey.AgeVerification);
    } else {
      setBlueprintsQuizV2Result(BlueprintKey.Kyc);
    }
  };

  // For Blueprints quiz V2 - adding a Blueprint to Sandbox
  const createBlueprintV2 = async (blueprintKey: string): Promise<void> => {
    await addBlueprint(blueprintKey);

    SegmentClient.sendSegmentTrackEvent('Blueprints Quiz V2 - Added Blueprint to Sandbox', {
      blueprintSourceType: blueprintKey,
    });

    return;
  };

  // For closing Blueprints quiz V2
  const closeBlueprintsQuizV2Modal = async () => {
    await loadOrganization();
    SegmentClient.sendSegmentTrackEvent('Blueprints Quiz V2 - Completed');
  };

  // For loading the Blueprint preview in BlueprintDemoModal
  const { callback: handleLoadBlueprintPreview, error: handleLoadBlueprintPreviewError } =
    useApiCallback(
      async (blueprintKey: string) => {
        await loadBlueprintPreview(blueprintKey);
      },
      [loadBlueprintPreview],
    );

  useApiErrorHandler({
    error: handleLoadBlueprintPreviewError,
    onError: (error) => {
      if (isPermissionsError(error)) {
        showMessage(permissionsErrorMessage(error), MessageType.Failure);
      } else {
        showMessage('Failed to load solution preview. Please try again.', MessageType.Failure);
      }
    },
    onNetworkError: () =>
      showMessage('We are experiencing network issues. Please try again.', MessageType.Failure),
  });

  // For loading the Inquiry Template preview in BlueprintDemoModal
  const {
    callback: handleLoadInquiryTemplateBuilderPreview,
    error: handleLoadInquiryTemplateBuilderPreviewError,
  } = useApiCallback(
    async (inquiryTemplateBuilderName: BuilderName) => {
      await loadInquiryTemplateBuilderPreview(inquiryTemplateBuilderName);
    },
    [loadInquiryTemplateBuilderPreview],
  );

  useApiErrorHandler({
    error: handleLoadInquiryTemplateBuilderPreviewError,
    onError: (error) => {
      if (isPermissionsError(error)) {
        showMessage(permissionsErrorMessage(error), MessageType.Failure);
      } else {
        showMessage(
          'Failed to load inquiry template preview. Please try again.',
          MessageType.Failure,
        );
      }
    },
    onNetworkError: () =>
      showMessage('We are experiencing network issues. Please try again.', MessageType.Failure),
  });

  return (
    <DashboardSandboxHomeView
      previouslyUsedBlueprints={blueprints}
      blueprintsCatalogServer={blueprintsCatalogServer}
      blueprintPreviews={blueprintPreviews}
      casesForSelectedBlueprint={casesForSelectedBlueprint}
      currentEnvironment={currentEnvironment}
      inquiriesForSelectedBlueprint={inquiriesForSelectedBlueprint}
      inquiryTemplateBuilderPreviews={inquiryTemplateBuilderPreviews}
      organization={organization}
      currentOrganizationPlan={currentOrganizationPlan}
      reportsForSelectedBlueprint={reportsForSelectedBlueprint}
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      users={users}
      workflowRunsForSelectedBlueprint={workflowRunsForSelectedBlueprint}
      isLoadingPreviouslyUsedBlueprints={isLoadingPreviouslyUsedBlueprints}
      isLoadingBlueprintsCatalog={isLoadingBlueprintsCatalog}
      isLoadingCasesForSelectedBlueprint={isLoadingCasesForSelectedBlueprint}
      isLoadingInquiriesForSelectedBlueprint={isLoadingInquiriesForSelectedBlueprint}
      isLoadingReportsForSelectedBlueprint={isLoadingReportsForSelectedBlueprint}
      isLoadingUsers={isLoadingUsers}
      isLoadingWorkflowRunsForSelectedBlueprint={isLoadingWorkflowRunsForSelectedBlueprint}
      isSavingCompletedStep={isSavingCompletedStep}
      blueprintsQuizV2SelectedIndustry={blueprintsQuizV2SelectedIndustry}
      blueprintsQuizV2SelectedUseCase={blueprintsQuizV2SelectedUseCase}
      blueprintsQuizV2ResultBlueprintKey={blueprintsQuizV2Result}
      handleBlueprintsQuizV2IndustrySubmit={handleBlueprintsQuizV2IndustrySubmit}
      handleBlueprintsQuizV2UseCaseSubmit={handleBlueprintsQuizV2UseCaseSubmit}
      createBlueprintV2={createBlueprintV2}
      closeBlueprintsQuizV2Modal={closeBlueprintsQuizV2Modal}
      handleLoadBlueprintPreview={handleLoadBlueprintPreview}
      handleLoadCasesForSelectedBlueprint={handleLoadCasesForSelectedBlueprint}
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      handleLoadInquiryTemplateBuilderPreview={handleLoadInquiryTemplateBuilderPreview}
      handleLoadInquiriesForSelectedBlueprint={handleLoadInquiriesForSelectedBlueprint}
      handleLoadReportsForSelectedBlueprint={handleLoadReportsForSelectedBlueprint}
      handleLoadUsers={handleLoadUsers}
      handleLoadWorkflowRunsForSelectedBlueprint={handleLoadWorkflowRunsForSelectedBlueprint}
      handleSaveCompletedStep={handleSaveOnboardingChecklistSandboxCompletedStep}
    />
  );
};

const mapStateToProps = (state: RootState) => {
  return {
    blueprints: state.blueprints.blueprints,
    blueprintPreviews: state.blueprints.blueprintPreviews,
    casesForSelectedBlueprint: state.cases.caseRequestKey
      ? state.cases.cases[state.cases.caseRequestKey] || []
      : [],
    currentEnvironment: state.currentEnvironment.currentEnvironment,
    currentOrganizationPlan: state.organization.currentOrganizationPlan,
    inquiriesForSelectedBlueprint:
      state.inquiries.inquiries[state.currentEnvironment.currentEnvironment.id] || [],
    inquiryTemplateBuilderPreviews: state.inquiryTemplates.templateBuilderPreviews,
    organization: state.organization.organization,
    reportsForSelectedBlueprint:
      state.reports.reports[state.currentEnvironment.currentEnvironment.id] || [],
    workflowRunsForSelectedBlueprint:
      state.workflowRuns.workflowRuns[state.currentEnvironment.currentEnvironment.id] || [],
  };
};

const mapDispatchToProps = {
  addBlueprint,
  loadBlueprints,
  loadBlueprintPreview,
  loadCasesList,
  loadInquiryList,
  loadInquiryTemplateBuilderPreview: loadTemplateBuilderPreview,
  loadOrganization,
  loadReportsList,
  loadWorkflowRunList,
  updateOnboardingChecklistSandboxCompletedSteps,
  showMessage,
};

const connector = connect(mapStateToProps, mapDispatchToProps);
type PropsFromRedux = ConnectedProps<typeof connector>;

export default connector(DashboardSandboxHomeRoute);
